import { isValidElement, useMemo, useState, type ReactNode } from 'react';

import { daysAgoInMY, dateTime, preciseTitle, todayInMY, type Timestamp } from '../lib/time';

export function Card({
  title,
  subtitle,
  children,
  actions,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {actions}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

/**
 * A cell may be given as a plain node, or as `{ sortValue, node }` when the value to sort by is
 * not recoverable from what is rendered.
 */
export type Cell = ReactNode | { sortValue: string | number | null; node: ReactNode };

/** Escape hatch for cells whose rendered text does not sort correctly. */
export function cell(sortValue: string | number | null, node: ReactNode): Cell {
  return { sortValue, node };
}

function isTagged(c: Cell): c is { sortValue: string | number | null; node: ReactNode } {
  return typeof c === 'object' && c !== null && !isValidElement(c) && 'sortValue' in c;
}

/** Flatten a rendered cell to its visible text, so a `<span>` sorts like the number inside it. */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement(node)) {
    return textOf((node.props as { children?: ReactNode }).children);
  }
  return '';
}

/** Values that mean "nothing here". Always sorted last, whichever direction is active. */
const BLANK = /^(|—|-|n\/a|never|null|undefined)$/i;

/**
 * Derive something comparable from a cell.
 *
 * Money and quantities reach this component already formatted — `money()` produces
 * "MYR 29,328,188". Comparing that as a string puts "MYR 9,000" above "MYR 29,328,188", so any
 * cell whose text is numeric once currency, separators and stray symbols are stripped is compared
 * as a NUMBER. ISO dates (2026-07-30) survive the same treatment as 20260730, which orders
 * correctly, so dates need no special case.
 */
function sortKey(c: Cell): { blank: boolean; num: number | null; str: string } {
  if (isTagged(c)) {
    const v = c.sortValue;
    if (v === null || v === undefined) return { blank: true, num: null, str: '' };
    if (typeof v === 'number') return { blank: !Number.isFinite(v), num: v, str: String(v) };
    return { blank: BLANK.test(v.trim()), num: null, str: v.toLowerCase() };
  }
  const raw = textOf(c).trim();
  if (BLANK.test(raw)) return { blank: true, num: null, str: '' };

  // Strip currency codes/symbols, thousands separators and percent signs, then decide.
  const stripped = raw.replace(/[^\d.,-]/g, '').replace(/,/g, '');
  const n = Number(stripped);
  const looksNumeric = stripped !== '' && stripped !== '-' && Number.isFinite(n);
  return { blank: false, num: looksNumeric ? n : null, str: raw.toLowerCase() };
}

type SortState = { col: number; dir: 'asc' | 'desc' } | null;

/**
 * Sortable table.
 *
 * Clicking a header cycles ascending → descending → unsorted, so the original order (which is
 * usually "ranked by the thing the panel is about") is always one more click away rather than
 * lost until refresh.
 *
 * Tables scroll inside their own container. A wide report must never make the whole page scroll
 * sideways — on a warehouse tablet that makes the nav unreachable.
 */
export function Table({
  head,
  rows,
  sortable = true,
}: {
  head: string[];
  rows: Cell[][];
  sortable?: boolean;
}) {
  const [sort, setSort] = useState<SortState>(null);

  const ordered = useMemo(() => {
    if (!sort) return rows;
    const { col, dir } = sort;
    const sign = dir === 'asc' ? 1 : -1;
    // Decorate with the original index so the sort is stable — rows that compare equal keep the
    // order the panel put them in, rather than shuffling on every re-render.
    return rows
      .map((r, i) => ({ r, i, k: sortKey(r[col] ?? null) }))
      .sort((a, b) => {
        // Blanks sink to the bottom in BOTH directions. Flipping a column should not float
        // "never" and "—" to the top.
        if (a.k.blank !== b.k.blank) return a.k.blank ? 1 : -1;
        if (a.k.blank) return a.i - b.i;
        if (a.k.num !== null && b.k.num !== null) {
          return a.k.num === b.k.num ? a.i - b.i : (a.k.num - b.k.num) * sign;
        }
        const c = a.k.str.localeCompare(b.k.str, undefined, { numeric: true });
        return c === 0 ? a.i - b.i : c * sign;
      })
      .map((d) => d.r);
  }, [rows, sort]);

  if (rows.length === 0) return <Empty>Nothing to show.</Empty>;

  const toggle = (col: number): void =>
    setSort((s) =>
      !s || s.col !== col ? { col, dir: 'asc' } : s.dir === 'asc' ? { col, dir: 'desc' } : null,
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
            {head.map((h, j) => {
              const active = sort?.col === j;
              return (
                <th
                  key={`${h}-${j}`}
                  scope="col"
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  className="whitespace-nowrap px-3 py-2 font-medium"
                >
                  {sortable && h !== '' ? (
                    <button
                      type="button"
                      onClick={() => toggle(j)}
                      className={`group inline-flex items-center gap-1 uppercase tracking-wide hover:text-brand-600 ${
                        active ? 'text-brand-600' : ''
                      }`}
                      title={`Sort by ${h}`}
                    >
                      {h}
                      <span aria-hidden="true" className={active ? '' : 'text-slate-300'}>
                        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    h
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordered.map((r, i) => (
            <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-brand-50">
              {r.map((c, j) => (
                <td key={j} className="whitespace-nowrap px-3 py-2 text-slate-700">
                  {isTagged(c) ? c.node : c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Loading() {
  return <p className="py-8 text-center text-sm text-slate-400">Loading…</p>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-slate-400">{children}</p>;
}

/**
 * An instant, in Malaysia time — the only way a timestamp reaches a table.
 *
 * Rendered to the minute because that is what fits a column, with the second and the underlying
 * UTC on hover: someone reconciling a scan against the PDA needs the second, someone reading a log
 * needs the UTC.
 *
 * Returns a tagged `Cell` rather than a component. `textOf` cannot see inside a function
 * component — it walks props.children, and a component's children are its inputs, not its output —
 * so a `<When/>` element would flatten to '' and the column would refuse to sort at all. Handing
 * the comparator the formatted string explicitly is both correct and honest about why.
 */
export function when(value: Timestamp, fallback = '—'): Cell {
  const text = dateTime(value, fallback);
  const title = preciseTitle(value);
  return cell(
    title ? text : null,
    title ? <span title={title}>{text}</span> : <span className="text-slate-400">{text}</span>,
  );
}

export type Range = { from: string; to: string; granularity: 'day' | 'week' | 'month' | 'year' };

export function useRange() {
  const [range, setRange] = useState<Range>(() => ({
    from: daysAgoInMY(730),
    to: todayInMY(),
    granularity: 'month',
  }));
  const params = useMemo(
    () =>
      new URLSearchParams({
        from: range.from,
        to: range.to,
        granularity: range.granularity,
      }).toString(),
    [range],
  );
  return { range, setRange, params };
}

export function RangePicker({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  // Bounds are Malaysian calendar days, because that is what the documents behind them are dated
  // in. Deriving them from the UTC clock returned yesterday's date for the whole morning shift,
  // quietly dropping the current day out of every default range.
  const preset = (months: number, granularity: Range['granularity']) => () =>
    onChange({
      from: daysAgoInMY(months * 30),
      to: todayInMY(),
      granularity,
    });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white px-5 py-3">
      <label className="text-xs text-slate-500">
        From
        <input
          type="date"
          value={range.from}
          onChange={(e) => onChange({ ...range, from: e.target.value })}
          className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm text-slate-900"
        />
      </label>
      <label className="text-xs text-slate-500">
        To
        <input
          type="date"
          value={range.to}
          onChange={(e) => onChange({ ...range, to: e.target.value })}
          className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm text-slate-900"
        />
      </label>
      <label className="text-xs text-slate-500">
        Group by
        <select
          value={range.granularity}
          onChange={(e) =>
            onChange({ ...range, granularity: e.target.value as Range['granularity'] })
          }
          className="mt-1 block rounded border border-slate-300 px-2 py-1 text-sm text-slate-900"
        >
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
      </label>
      <div className="ml-auto flex gap-2">
        <button
          onClick={preset(3, 'week')}
          className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-brand-50"
        >
          3 months
        </button>
        <button
          onClick={preset(12, 'month')}
          className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-brand-50"
        >
          12 months
        </button>
        <button
          onClick={preset(60, 'year')}
          className="rounded border border-slate-300 px-3 py-1 text-xs hover:bg-brand-50"
        >
          5 years
        </button>
      </div>
    </div>
  );
}
